// Client-side cryptography. The server is oblivious to content: every message
// and file blob is encrypted here, in the browser, before it is sent.
//
// This build ships the PSK (pre-shared key) backend fully: a passphrase shared
// out of band is stretched with PBKDF2 into an AES-256-GCM room key, and each
// message/file is sealed under a fresh random IV. The OpenPGP and double-ratchet
// backends are stubbed for now (see `createCipher` / `pgp` below).

import type { CipherMode } from "./api";
import type { WireMessage } from "./api";

export type { CipherMode } from "./api";

export interface FileRef {
  id: string;
  name: string;
  mime?: string;
  size: number;
  // ArrayBuffer-backed so they satisfy WebCrypto's BufferSource (TS 5.7+).
  key: Uint8Array<ArrayBuffer>;
  iv: Uint8Array<ArrayBuffer>;
}

export interface Envelope {
  v: number;
  handle: string;
  body: string;
  file?: FileRef;
  ts: number;
}

export interface RoomCipher {
  openMessage(wire: WireMessage): Promise<Envelope>;
  sealMessage(env: Envelope): Promise<{ iv: string; ciphertext: string }>;
}

// Human-readable names for the cipher modes. Modes not yet implemented in this
// build are marked so the UI sets expectations honestly.
export const CIPHER_LABELS: Record<CipherMode, string> = {
  psk: "Pre-shared key",
  pgp: "OpenPGP (coming soon)",
  ratchet: "Double ratchet (coming soon)",
};

const PBKDF2_ITERATIONS = 310_000;
const IV_BYTES = 12;

// --- base64 <-> bytes (small payloads only: ivs, keys, message envelopes) ---
function bytesToB64(bytes: Uint8Array): string {
  let s = "";
  for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i]);
  return btoa(s);
}

function b64ToBytes(b64: string): Uint8Array<ArrayBuffer> {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

// UTF-8 encode into a fresh ArrayBuffer-backed view (BufferSource-compatible).
function utf8(s: string): Uint8Array<ArrayBuffer> {
  return new Uint8Array(new TextEncoder().encode(s));
}

// FileRef carries raw-byte key/iv that don't survive JSON.stringify intact, so
// the on-the-wire envelope encodes them as base64 and we restore them on open.
function envelopeToJson(env: Envelope): string {
  const file = env.file
    ? { ...env.file, key: bytesToB64(env.file.key), iv: bytesToB64(env.file.iv) }
    : undefined;
  return JSON.stringify({ ...env, file });
}

function envelopeFromJson(json: string): Envelope {
  const raw = JSON.parse(json) as Omit<Envelope, "file"> & {
    file?: Omit<FileRef, "key" | "iv"> & { key: string; iv: string };
  };
  const file: FileRef | undefined = raw.file
    ? { ...raw.file, key: b64ToBytes(raw.file.key), iv: b64ToBytes(raw.file.iv) }
    : undefined;
  return { ...raw, file };
}

// --- PSK backend ---------------------------------------------------------
async function deriveRoomKey(passphrase: string, saltB64: string): Promise<CryptoKey> {
  const salt = b64ToBytes(saltB64);
  const baseKey = await crypto.subtle.importKey("raw", utf8(passphrase), "PBKDF2", false, [
    "deriveKey",
  ]);
  return crypto.subtle.deriveKey(
    { name: "PBKDF2", salt, iterations: PBKDF2_ITERATIONS, hash: "SHA-256" },
    baseKey,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"],
  );
}

function pskCipher(key: CryptoKey): RoomCipher {
  return {
    async sealMessage(env: Envelope) {
      const iv = crypto.getRandomValues(new Uint8Array(IV_BYTES));
      const plaintext = utf8(envelopeToJson(env));
      const ct = await crypto.subtle.encrypt({ name: "AES-GCM", iv }, key, plaintext);
      return { iv: bytesToB64(iv), ciphertext: bytesToB64(new Uint8Array(ct)) };
    },
    async openMessage(wire: WireMessage) {
      const iv = b64ToBytes(wire.iv);
      const ct = b64ToBytes(wire.ciphertext);
      const plaintext = await crypto.subtle.decrypt({ name: "AES-GCM", iv }, key, ct);
      return envelopeFromJson(new TextDecoder().decode(plaintext));
    },
  };
}

export interface CreateCipherOptions {
  mode: CipherMode;
  roomId: string;
  saltB64: string;
  passphrase: string;
  isInitiator: boolean;
}

export async function createCipher(opts: CreateCipherOptions): Promise<RoomCipher> {
  if (opts.mode === "psk") {
    const key = await deriveRoomKey(opts.passphrase, opts.saltB64);
    return pskCipher(key);
  }
  throw new Error(
    `${CIPHER_LABELS[opts.mode]} isn't available in this build yet — create a PSK room instead.`,
  );
}

// --- file encryption (mode-independent: fresh per-file AES-256-GCM key) ---
export async function encryptFile(
  bytes: ArrayBuffer,
): Promise<{ bytes: Uint8Array<ArrayBuffer>; key: Uint8Array<ArrayBuffer>; iv: Uint8Array<ArrayBuffer> }> {
  const key = crypto.getRandomValues(new Uint8Array(32));
  const iv = crypto.getRandomValues(new Uint8Array(IV_BYTES));
  const cryptoKey = await crypto.subtle.importKey("raw", key, "AES-GCM", false, ["encrypt"]);
  const ct = await crypto.subtle.encrypt({ name: "AES-GCM", iv }, cryptoKey, bytes);
  return { bytes: new Uint8Array(ct), key, iv };
}

export async function decryptFile(
  key: Uint8Array<ArrayBuffer>,
  iv: Uint8Array<ArrayBuffer>,
  ciphertext: Uint8Array<ArrayBuffer>,
): Promise<Uint8Array<ArrayBuffer>> {
  const cryptoKey = await crypto.subtle.importKey("raw", key, "AES-GCM", false, ["decrypt"]);
  const plaintext = await crypto.subtle.decrypt({ name: "AES-GCM", iv }, cryptoKey, ciphertext);
  return new Uint8Array(plaintext);
}

// --- standalone PSK blob (transcripts) -----------------------------------
// Decrypt a {salt, iv, ct} wrapper (all base64) produced by the transcripts
// sync job under a passphrase. Same PBKDF2 -> AES-256-GCM scheme as rooms, so
// the server only ever holds these ciphertext blobs.
export async function decryptBlobWithPassphrase(
  wrapped: string,
  passphrase: string,
): Promise<string> {
  const obj = JSON.parse(wrapped) as { salt: string; iv: string; ct: string };
  const key = await deriveRoomKey(passphrase, obj.salt);
  const plaintext = await crypto.subtle.decrypt(
    { name: "AES-GCM", iv: b64ToBytes(obj.iv) },
    key,
    b64ToBytes(obj.ct),
  );
  return new TextDecoder().decode(plaintext);
}

// --- OpenPGP identity/recipient management (stubbed for this build) -------
// Kept as a benign no-op surface so the PGP panel renders without crashing.
// Read accessors return empty; mutating actions explain that PGP isn't ready.
export interface Identity {
  fingerprint: string;
  publicKeyArmored: string;
}

const PGP_UNAVAILABLE = "OpenPGP mode isn't available in this build yet.";

export const pgp = {
  getStoredIdentity(): Identity | null {
    return null;
  },
  async generateIdentity(_name: string, _passphrase: string): Promise<Identity> {
    throw new Error(PGP_UNAVAILABLE);
  },
  async importIdentity(_armoredPrivateKey: string): Promise<Identity> {
    throw new Error(PGP_UNAVAILABLE);
  },
  clearStoredIdentity(): void {
    /* nothing stored */
  },
  getRoomRecipients(_roomId: string): string[] {
    return [];
  },
  async recipientFingerprints(_roomId: string): Promise<string[]> {
    return [];
  },
  async addRoomRecipient(_roomId: string, _armoredPublicKey: string): Promise<void> {
    throw new Error(PGP_UNAVAILABLE);
  },
  removeRoomRecipient(_roomId: string, _index: number): void {
    /* nothing stored */
  },
};
